import { Component } from 'react'
import DarkModeToggle from 'react-dark-mode-toggle'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { Router } from 'react-router-dom'
import Routes from './routes'
import History from './routes/history'
import { pageSections, pagesBySection } from './routes/pages'
import { HiWifi, HiOutlineMenu, HiBell } from 'react-icons/hi'
import { IUser, getSessionUser, logout } from './api/auth'
import LogoImg from './static/linetech_logo.png'
import Loader from './components/loader'
import { initWsClient, wsClient } from './wsclient'
import { onLocaleChange, getLocale, getString } from './locale'
import LocaleSelect from './components/localeselect'
import {
    decideDeviceApprovalNotification,
    listNotifications,
    markNotificationRead,
    type INotificationItem,
} from './api/security'
import { onNotificationChanged } from './wsclient/handlers/notification-changed.handler'
import { onCustomerConnect, clearCustomerConnect, CustomerState, type IListenerUID } from './wsclient/handlers/customertraffic.handler'

export let appRef: App

export enum Theme {
    Light = 'light',
    Dark = 'dark',
}

enum AppState {
    Loading = 'loading',
    ServerOffline = 'server_offline',
    Ok = 'ok',
}

type State = {
    theme: Theme
    header?: {
        icon: JSX.Element
        label: () => string
    }
    user?: IUser
    appState: AppState
    showMobileMenu: boolean
    currentPath: string
    notificationsOpen: boolean
    loadingNotifications: boolean
    notifications: INotificationItem[]
    unreadNotificationCount: number
}

type Props = {}

type RefreshNotificationsOptions = {
    showLoading?: boolean
}

function hasPermission(user: IUser | undefined, required?: string): boolean {
    if (!required) return true
    const keys = user?.permissionKeys || []
    const denied = user?.deniedPermissionKeys || []
    // Explicit denies take priority over hierarchical grants.
    if (denied.includes(required)) return false
    if (denied.some(d => required.startsWith(`${d}.`))) return false
    if (keys.includes(required)) return true
    return keys.some(key => required.startsWith(`${key}.`))
}

class App extends Component<Props, State> {
    private unlistenHistory?: () => void
    private notificationsContainerRef: HTMLDivElement | null = null
    private unsubscribeNotificationChanged?: () => void
    private customerTrafficListenerId?: IListenerUID

    state: State = {
        theme: localStorage.theme,
        header: {
            icon: <HiWifi className="me-2" size={28} />,
            label: () => getString('pagelabels.dashboard_internal'),
        },
        appState: AppState.Loading,
        showMobileMenu: false,
        currentPath: History.location?.pathname || '/',
        notificationsOpen: false,
        loadingNotifications: false,
        notifications: [],
        unreadNotificationCount: 0,
    }

    constructor(props: Props) {
        super(props)
        appRef = this
    }

    componentDidMount = () => {
        this.updateTheme()
        this.loadUser()

        onLocaleChange(() => {
            this.forceUpdate()
        })

        this.unlistenHistory = History.listen(location => {
            this.setState({ currentPath: location.pathname, showMobileMenu: false, notificationsOpen: false })
        })

        document.addEventListener('mousedown', this.handleGlobalMouseDown)
        window.addEventListener('focus', this.handleWindowFocus)
        document.addEventListener('visibilitychange', this.handleVisibilityChange)
        this.unsubscribeNotificationChanged = onNotificationChanged(() => {
            void this.refreshPendingApprovals()
        })
        this.customerTrafficListenerId = onCustomerConnect((client, state) => {
            this.handleCustomerDisconnectKickout(client.id, state)
        })
    }

    componentWillUnmount() {
        this.unlistenHistory?.()
        document.removeEventListener('mousedown', this.handleGlobalMouseDown)
        window.removeEventListener('focus', this.handleWindowFocus)
        document.removeEventListener('visibilitychange', this.handleVisibilityChange)
        this.unsubscribeNotificationChanged?.()
        this.unsubscribeNotificationChanged = undefined
        if (this.customerTrafficListenerId !== undefined) {
            clearCustomerConnect(this.customerTrafficListenerId)
            this.customerTrafficListenerId = undefined
        }
    }

    private handleCustomerDisconnectKickout(clientId: string, state: CustomerState) {
        if (state !== CustomerState.Disconnected) return
        const pathname = History.location?.pathname || this.state.currentPath || ''
        const match = pathname.match(/^\/client\/([^/]+)$/)
        if (!match) return
        const openedClientId = decodeURIComponent(match[1] || '')
        if (openedClientId !== clientId) return
        toast.error(getString('client.disconnect_message', clientId))
        History.push('/')
    }

    private handleGlobalMouseDown = (event: MouseEvent) => {
        if (!this.state.notificationsOpen) return
        if (!this.notificationsContainerRef) return
        const target = event.target as Node | null
        if (target && !this.notificationsContainerRef.contains(target)) {
            this.setState({ notificationsOpen: false })
        }
    }

    private handleWindowFocus = () => {
        void this.refreshPendingApprovals()
    }

    private handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            void this.refreshPendingApprovals()
        }
    }

    async loadUser() {
        try {
            const result = await getSessionUser()
            console.log('result', result)
            this.setState({ appState: AppState.Ok, user: result.data }, () => {
                if (result.data !== undefined) {
                    initWsClient(result.data.sessionId)
                    void this.refreshPendingApprovals()
                }
            })
        } catch (err) {
            this.setState({ appState: AppState.ServerOffline })
            console.error('Failed to load user', err)
            toast.error('Failed to load user')
        }
    }

    public setHeader(icon: JSX.Element, label: () => string) {
        return this.setState({ header: { icon, label } })
    }

    public setUser(user: IUser) {
        initWsClient(user.sessionId)
        return this.setState({ user }, () => {
            void this.refreshPendingApprovals()
        })
    }

    private updateTheme() {
        if (localStorage.theme === Theme.Dark || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
            document.documentElement.classList.add(Theme.Dark)
            this.setState({ theme: Theme.Dark })
        } else {
            document.documentElement.classList.remove(Theme.Dark)
            this.setState({ theme: Theme.Light })
        }
    }

    public async logout() {
        try {
            await logout()
            wsClient.close()
        } catch (err) {
            console.error(err)
            toast.error((err as Error).message)
        }
        this.setState({ user: undefined, notifications: [], unreadNotificationCount: 0, notificationsOpen: false })
        History.push('/login')
    }

    private canSeeApprovalNotifications(): boolean {
        const user = this.state.user
        if (!user) return false
        return hasPermission(user, 'users.manage') || hasPermission(user, 'page.users_management')
    }

    private notificationsEqual(a: INotificationItem[], b: INotificationItem[]): boolean {
        if (a.length !== b.length) return false
        for (let i = 0; i < a.length; i += 1) {
            const left = a[i]
            const right = b[i]
            if (!left || !right) return false
            if (
                left.id !== right.id ||
                left.isRead !== right.isRead ||
                left.type !== right.type ||
                left.title !== right.title ||
                left.message !== right.message ||
                left.createdAt !== right.createdAt
            ) {
                return false
            }
        }
        return true
    }

    private async refreshPendingApprovals(options: RefreshNotificationsOptions = {}) {
        if (!this.canSeeApprovalNotifications()) {
            if (this.state.notifications.length > 0 || this.state.unreadNotificationCount > 0) {
                this.setState({ notifications: [], unreadNotificationCount: 0 })
            }
            return
        }
        const showLoading = !!options.showLoading
        try {
            if (showLoading && !this.state.loadingNotifications) {
                this.setState({ loadingNotifications: true })
            }
            const result = await listNotifications()
            const nextNotifications = result.data.items || []
            const nextUnread = Number(result.data.unreadCount || 0)
            const hasChanged =
                nextUnread !== this.state.unreadNotificationCount ||
                !this.notificationsEqual(this.state.notifications, nextNotifications)
            if (!hasChanged && (!showLoading || !this.state.loadingNotifications)) {
                return
            }
            this.setState({
                notifications: nextNotifications,
                unreadNotificationCount: nextUnread,
                loadingNotifications: false,
            })
        } catch {
            if (showLoading && this.state.loadingNotifications) {
                this.setState({ loadingNotifications: false })
            }
        }
    }

    private async decideApproval(notification: INotificationItem, approved: boolean) {
        try {
            await decideDeviceApprovalNotification(notification.id, approved)
            toast.success(
                approved
                    ? getLocale() === 'he'
                        ? 'המכשיר אושר'
                        : 'Device approved'
                    : getLocale() === 'he'
                        ? 'המכשיר נדחה'
                        : 'Device denied',
            )
            await this.refreshPendingApprovals()
        } catch (error: any) {
            toast.error(error?.message || (getLocale() === 'he' ? 'עדכון מכשיר נכשל' : 'Failed to update device'))
        }
    }

    render() {
        const { appState } = this.state
        return (
            <div dir={getLocale() === 'he' ? 'rtl' : 'ltr'} className="min-h-screen bg-gray-100 dark:bg-gray-900">
                {appState !== AppState.Ok ? this.renderStatus() : this.renderLayout()}
                {this.renderToast()}
            </div>
        )
    }

    private renderLayout() {
        if (this.state.currentPath === '/login') {
            return this.renderLoginLayout()
        }

        const isRtl = getLocale() === 'he'
        const isDark = this.state.theme === Theme.Dark
        const mobileSideClass = isRtl ? 'right-0' : 'left-0'
        const sidebarSurfaceClass = isDark ? 'bg-black text-gray-100 border-gray-900' : 'bg-white text-gray-900 border-gray-200'
        return (
            <div className="min-h-screen flex">
                <aside className={`hidden md:flex md:w-72 md:flex-col border-e shadow-xl ${sidebarSurfaceClass}`}>
                    {this.renderSidebarContent()}
                </aside>

                {this.state.showMobileMenu && (
                    <div className="md:hidden fixed inset-0 z-40">
                        <div className="absolute inset-0 bg-black/50" onClick={() => this.setState({ showMobileMenu: false })} />
                        <aside
                            className={`absolute top-0 ${mobileSideClass} h-full w-80 max-w-[88vw] border-e flex flex-col shadow-2xl ${sidebarSurfaceClass}`}
                        >
                            {this.renderSidebarContent(true)}
                        </aside>
                    </div>
                )}

                <div className="flex-1 min-w-0 flex flex-col">
                    <div className={`md:hidden border-b px-4 py-3 flex items-center justify-between ${isDark ? 'bg-black text-gray-100 border-gray-900' : 'bg-white text-gray-900 border-gray-200'}`}>
                        <img className="h-10 w-auto" src={LogoImg} alt="Workflow" />
                        <button
                            title="menu"
                            className={`${isDark ? 'text-gray-100 hover:text-white focus:text-white' : 'text-gray-700 hover:text-gray-900 focus:text-gray-900'} focus:outline-none`}
                            onClick={() => this.setState({ showMobileMenu: true })}
                        >
                            <HiOutlineMenu className="h-6 w-6" />
                        </button>
                    </div>
                    {this.renderHeader()}
                    {this.renderMain()}
                </div>
            </div>
        )
    }

    private renderLoginLayout() {
        return (
            <main className="min-h-screen">
                <Router history={History}>
                    <Routes />
                </Router>
            </main>
        )
    }

    private renderStatus() {
        const { appState } = this.state
        return (
            <div className="h-screen w-full flex items-center justify-center">
                {appState === AppState.Loading ? <Loader size={24} /> : <h1>The server seems to be offline, try to refresh the page.</h1>}
            </div>
        )
    }

    private renderToast() {
        return <ToastContainer position="top-center" rtl={getLocale() === 'he'} />
    }

    private renderSidebarContent(isMobile = false) {
        const currentPath = this.state.currentPath
        const isDark = this.state.theme === Theme.Dark
        const sectionLabelClass = isDark ? 'text-gray-300' : 'text-gray-600'
        const inactiveItemClass = isDark
            ? 'bg-gray-900 text-gray-100 hover:bg-gray-800'
            : 'bg-gray-50 text-gray-800 hover:bg-gray-100'
        const footerClass = isDark ? 'border-gray-900 bg-black' : 'border-gray-200 bg-gray-50'
        return (
            <>
                <div className={`px-4 py-4 border-b ${isDark ? 'border-gray-900' : 'border-gray-200'}`}>
                    <div className={`inline-flex rounded-lg px-3 py-2 ${isDark ? 'bg-gray-100' : 'bg-gray-50 border border-gray-200'}`}>
                        <img className="h-12 w-auto" src={LogoImg} alt="Workflow" />
                    </div>
                    {this.state.user && (
                        <div className={`mt-3 px-1 text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>{this.state.user.username}</div>
                    )}
                </div>
                <div className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
                    {this.state.user &&
                        pageSections.map(section => {
                            const sectionPages = pagesBySection(section.id)
                            if (sectionPages.length === 0) return undefined
                            return (
                                <div key={section.id} className="space-y-1">
                                    <div className={`px-3 text-[11px] uppercase tracking-wide font-semibold ${sectionLabelClass}`}>
                                        {section.label()}
                                    </div>
                                    {sectionPages
                                        .filter(page => hasPermission(this.state.user, page.permissionKey))
                                        .map(page => {
                                            const active = currentPath === page.path
                                            return (
                                                <button
                                                    key={page.path}
                                                    className={`w-full text-start px-3 py-2 rounded-md text-sm font-medium transition-colors ${active ? 'bg-indigo-600 text-white shadow-sm' : inactiveItemClass
                                                        }`}
                                                    onClick={() => {
                                                        if (page.externalHref) {
                                                            window.location.href = page.externalHref
                                                            return
                                                        }
                                                        if (isMobile) {
                                                            this.setState({ showMobileMenu: false }, () => History.push(page.path))
                                                            return
                                                        }
                                                        History.push(page.path)
                                                    }}
                                                >
                                                    {page.label()}
                                                </button>
                                            )
                                        })}
                                </div>
                            )
                        })}
                </div>
                <div className={`px-4 py-3 border-t space-y-3 ${footerClass}`}>
                    <div className="flex items-center justify-between">
                        {this.renderLocaleSelect('me-0')}
                        {this.renderThemeSwitch('me-0')}
                    </div>
                    <button
                        onClick={this.logout.bind(this)}
                        className={`w-full rounded-md px-3 py-2 text-sm font-semibold transition-colors ${isDark ? 'bg-gray-900 border border-gray-800 text-gray-100 hover:bg-gray-800' : 'bg-white border border-gray-200 text-gray-800 hover:bg-gray-100'
                            }`}
                    >
                        {getString('common.logout')}
                    </button>
                </div>
            </>
        )
    }

    private renderHeader() {
        const { header, notificationsOpen, notifications, unreadNotificationCount, loadingNotifications } = this.state
        const isDark = this.state.theme === Theme.Dark
        return (
            <header className="bg-white dark:bg-black shadow relative">
                <div className="max-w-7xl mx-auto py-2 px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-3">
                    <h1 className="flex text-xl font-bold leading-tight text-gray-900 dark:text-gray-50">
                        {header?.icon} {header?.label()}
                    </h1>
                    {this.canSeeApprovalNotifications() && (
                        <div className="relative" ref={node => (this.notificationsContainerRef = node)}>
                            <button
                                onClick={() =>
                                    this.setState(
                                        prev => ({ notificationsOpen: !prev.notificationsOpen }),
                                        () => {
                                            if (this.state.notificationsOpen) {
                                                void this.refreshPendingApprovals({ showLoading: true })
                                            }
                                        },
                                    )
                                }
                                className={`relative rounded-full p-2 border ${isDark
                                        ? 'border-gray-700 bg-gray-900 text-gray-100 hover:bg-gray-800'
                                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                    }`}
                                title={getLocale() === 'he' ? 'התראות' : 'Notifications'}
                            >
                                <HiBell size={20} />
                                {unreadNotificationCount > 0 && (
                                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[11px] leading-[18px] text-center">
                                        {unreadNotificationCount}
                                    </span>
                                )}
                            </button>
                            {notificationsOpen && (
                                <div
                                    style={{ width: '32rem', maxWidth: '95vw' }}
                                    className={`absolute z-50 mt-2 ${getLocale() === 'he' ? 'left-0' : 'right-0'
                                        } rounded-xl border shadow-xl ${isDark ? 'border-gray-700 bg-gray-900 text-gray-100' : 'border-gray-200 bg-white text-gray-900'
                                        }`}
                                >
                                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 font-semibold">
                                        {getLocale() === 'he' ? 'התראות' : 'Notifications'}
                                    </div>
                                    <div className="max-h-96 overflow-auto p-2 space-y-2">
                                        {loadingNotifications && (
                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                {getLocale() === 'he' ? 'טוען...' : 'Loading...'}
                                            </div>
                                        )}
                                        {!loadingNotifications && notifications.length === 0 && (
                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                {getLocale() === 'he' ? 'אין התראות חדשות' : 'No new notifications'}
                                            </div>
                                        )}
                                        {!loadingNotifications &&
                                            notifications.map(notification => {
                                                const payload = (notification.payload || {}) as Record<string, unknown>
                                                const deviceName =
                                                    `${payload.deviceName ?? ''}`.trim() || (getLocale() === 'he' ? 'מכשיר לא מזוהה' : 'Unknown device')
                                                const username = `${payload.username ?? ''}`.trim()
                                                const userId = `${payload.userId ?? '-'}`
                                                const platform = `${payload.devicePlatform ?? '-'}`
                                                const ipAddress = `${payload.ipAddress ?? '-'}`
                                                const isDeviceApproval = notification.type === 'device_approval_pending'
                                                return (
                                                    <div key={notification.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-2">
                                                        <div className="text-sm font-semibold">
                                                            {isDeviceApproval ? deviceName : (notification.title || (getLocale() === 'he' ? 'התראה' : 'Notification'))}
                                                        </div>
                                                        {isDeviceApproval ? (
                                                            <>
                                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                    {(username
                                                                        ? getLocale() === 'he'
                                                                            ? `משתמש: ${username}`
                                                                            : `User: ${username}`
                                                                        : (getLocale() === 'he' ? 'משתמש #' : 'User #') + userId) +
                                                                        ' • ' +
                                                                        platform}
                                                                </div>
                                                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{ipAddress}</div>
                                                            </>
                                                        ) : (
                                                            <div className="text-xs text-gray-500 dark:text-gray-400">{notification.message || '-'}</div>
                                                        )}
                                                        <div className="mt-2 flex gap-2">
                                                            {isDeviceApproval && (
                                                                <>
                                                                    <button
                                                                        className="rounded bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1"
                                                                        onClick={() => this.decideApproval(notification, true)}
                                                                    >
                                                                        {getLocale() === 'he' ? 'אשר' : 'Approve'}
                                                                    </button>
                                                                    <button
                                                                        className="rounded bg-red-600 hover:bg-red-700 text-white text-xs px-2 py-1"
                                                                        onClick={() => this.decideApproval(notification, false)}
                                                                    >
                                                                        {getLocale() === 'he' ? 'דחה' : 'Deny'}
                                                                    </button>
                                                                </>
                                                            )}
                                                            {!notification.isRead && (
                                                                <button
                                                                    className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs px-2 py-1"
                                                                    onClick={async () => {
                                                                        try {
                                                                            await markNotificationRead(notification.id)
                                                                            await this.refreshPendingApprovals()
                                                                        } catch (error: any) {
                                                                            toast.error(error?.message || (getLocale() === 'he' ? 'עדכון הודעה נכשל' : 'Failed to update notification'))
                                                                        }
                                                                    }}
                                                                >
                                                                    {getLocale() === 'he' ? 'סמן כנקרא' : 'Mark as read'}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </header>
        )
    }

    private renderMain() {
        return (
            <main className="flex-1 overflow-auto">
                <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                    <div className="px-4 sm:px-0">
                        <Router history={History}>
                            <Routes />
                        </Router>
                    </div>
                </div>
            </main>
        )
    }

    private renderThemeSwitch(className = 'me-6') {
        const { theme } = this.state
        return (
            <div className={`flex items-center justify-center ${className}`}>
                <DarkModeToggle
                    className="focus:outline-none ltr"
                    onChange={darkMode => {
                        const theme: State['theme'] = darkMode ? Theme.Dark : Theme.Light
                        this.setState({ theme })
                        localStorage.theme = theme
                        this.updateTheme()
                    }}
                    checked={theme === Theme.Dark}
                    size={80}
                />
            </div>
        )
    }

    private renderLocaleSelect(className = 'me-6') {
        return <LocaleSelect className={className} />
    }
}

export default App
